import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Consent() {
  const { token } = useParams();
  const [c, setC] = useState(undefined); // undefined=loading, null=invalid
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ display_as_confirmed: "", agree: false, redaction_requests: "", signed_name: "", guardian_name: "", guardian_relationship: "", guardian_is_minor_ack: false });

  useEffect(() => {
    api.get(`/consent/${token}`).then((r) => {
      setC(r.data);
      setF((p) => ({ ...p, display_as_confirmed: r.data.display_as || "" }));
      if (r.data.status === "signed") setDone(true);
    }).catch(() => setC(null));
  }, [token]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const guardian = c?.form_type === "guardian";
  const field = "mt-1 w-full border-2 border-[#D3CFC0] bg-white text-[#222222] px-4 py-3 focus:outline-none focus:border-[#222222]";
  const label = "font-head uppercase tracking-wide text-xs text-[#222222]";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree || !f.signed_name.trim()) { toast.error("Please agree and type your name to sign."); return; }
    setBusy(true);
    try { await api.post(`/consent/${token}/sign`, f); setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  if (c === undefined) return <div className="max-w-2xl mx-auto px-4 py-20 text-[#6E6E6E]">Loading…</div>;
  if (c === null) return <div className="max-w-2xl mx-auto px-4 py-20 text-center"><h1 className="font-head uppercase text-2xl text-[#222222]">This consent link is invalid or expired.</h1></div>;
  if (done) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="consent-done">
      <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "#DB9130", color: "#222222" }}><Check size={28} strokeWidth={3} /></div>
      <h1 className="mt-6 font-head font-semibold uppercase text-3xl text-[#222222]">Signed — thank you.</h1>
      <p className="mt-3 text-[#5B6770]">Your consent has been recorded. You can request removal of any published content later by emailing info@voluntoldhq.com.</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="consent-page">
      <Seo title="Consent — The Voluntold Ring" description="Voluntold Ring consent form." path={`/consent/${token}`} noindex />
      <span className="font-head uppercase tracking-[0.2em] text-xs text-[#DB9130]">The Voluntold Ring</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-3xl text-[#222222]">{guardian ? "Parental / Guardian Consent" : "Media Release"}</h1>
      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className={label}>Your name, role & team/program as it should appear publicly</label>
          <input required value={f.display_as_confirmed} onChange={(e) => set("display_as_confirmed", e.target.value)} data-testid="consent-display" className={field} />
        </div>
        {guardian && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Parent/Guardian name</label><input required value={f.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} data-testid="consent-guardian-name" className={field} /></div>
            <div><label className={label}>Relationship to participant</label><input value={f.guardian_relationship} onChange={(e) => set("guardian_relationship", e.target.value)} data-testid="consent-guardian-rel" className={field} /></div>
          </div>
        )}
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={f.agree} onChange={(e) => set("agree", e.target.checked)} data-testid="consent-agree" className="mt-1 w-4 h-4 accent-[#DB9130]" />
          <span className="text-sm text-[#2D2D2D] leading-relaxed">I agree that Voluntold may record, edit, and publish video/photos of me, my story, and quotes from my nomination across VoluntoldHQ's website and social channels.</span>
        </label>
        {guardian && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={f.guardian_is_minor_ack} onChange={(e) => set("guardian_is_minor_ack", e.target.checked)} data-testid="consent-minor-ack" className="mt-1 w-4 h-4 accent-[#DB9130]" />
            <span className="text-sm text-[#2D2D2D] leading-relaxed">I confirm the participant is a minor, that participation is voluntary, and that consent can be withdrawn at any time.</span>
          </label>
        )}
        <div>
          <label className={label}>Redaction requests (optional)</label>
          <p className="text-xs text-[#6E6E6E] mt-0.5">e.g. "don't mention my employer," "blur my face in team photos."</p>
          <textarea rows={3} value={f.redaction_requests} onChange={(e) => set("redaction_requests", e.target.value)} data-testid="consent-redaction" className={field} />
        </div>
        <div>
          <label className={label}>Type your full name to sign</label>
          <input required value={f.signed_name} onChange={(e) => set("signed_name", e.target.value)} data-testid="consent-signature" className={field} placeholder="Your full name" />
        </div>
        <p className="text-xs text-[#6E6E6E]">You can request removal of published content later by emailing info@voluntoldhq.com.</p>
        <button type="submit" disabled={busy} data-testid="consent-submit" className="inline-flex items-center gap-2 bg-[#DB9130] text-[#222222] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#C67F22] transition-colors disabled:opacity-60">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Sign & Submit"}
        </button>
      </form>
    </div>
  );
}
